import apiClient from "../baseUrl/axiosConfig";

// The hosting stack drops any request that runs longer than five minutes, so a
// large sample on a mobile connection cannot arrive as one upload. Samples are
// sent in small pieces instead: every request stays short, and a piece that
// fails on a flaky connection is retried on its own.
const FALLBACK_CHUNK_KB = 2048;
const CHUNK_ATTEMPTS = 3;
// A piece is abandoned only when it stops making progress, so a slow but
// healthy connection is never cut off mid-transfer.
const CHUNK_STALL_MS = 60000;
const FORM_TIMEOUT_MS = 120000;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const newUploadId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();

  // Older mobile browsers: a UUIDv4 shape is all the server requires.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
};

// An applicant may attach several files, so every upload failure carries the
// name of the file it happened on — otherwise the message says a sample failed
// without saying which one to replace.
const uploadFailure = (error, fileName) => {
  // A validation error from the server (too large, wrong type) is final and
  // carries a message worth showing; anything else is a transport problem.
  const status = error.response ? error.response.status : null;

  if (status === 422) {
    const failure = new Error("sample rejected");
    failure.sampleRejected = true;
    failure.fileName = fileName;
    failure.message =
      (error.response.data && error.response.data.message) || "sample rejected";
    return failure;
  }

  const failure = new Error("upload interrupted");
  failure.uploadStalled = error.stalled === true || !error.response;
  failure.fileName = fileName;
  failure.response = error.response;
  return failure;
};

const sendChunk = async ({ uploadId, index, total, file, chunk, onLoaded }) => {
  const body = new FormData();
  body.append("upload_id", uploadId);
  body.append("index", index);
  body.append("total", total);
  body.append("file_name", file.name);
  body.append("chunk", chunk, `${index}.part`);

  const controller = new AbortController();
  let stalled = false;
  let watchdog = setTimeout(() => {
    stalled = true;
    controller.abort();
  }, CHUNK_STALL_MS);

  const keepAlive = () => {
    clearTimeout(watchdog);
    watchdog = setTimeout(() => {
      stalled = true;
      controller.abort();
    }, CHUNK_STALL_MS);
  };

  try {
    return await apiClient.post("/voice-talents/samples/chunk", body, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 0,
      signal: controller.signal,
      onUploadProgress: (event) => {
        keepAlive();
        onLoaded(Math.min(event.loaded, chunk.size));
      },
    });
  } catch (error) {
    if (stalled) {
      const failure = new Error("chunk stalled");
      failure.stalled = true;
      throw failure;
    }

    throw error;
  } finally {
    clearTimeout(watchdog);
  }
};

const uploadInPieces = async ({ file, chunkBytes, onBytes }) => {
  const uploadId = newUploadId();
  const total = Math.max(1, Math.ceil(file.size / chunkBytes));
  let settled = 0;

  for (let index = 0; index < total; index++) {
    const chunk = file.slice(index * chunkBytes, (index + 1) * chunkBytes);
    let lastError = null;

    for (let attempt = 1; attempt <= CHUNK_ATTEMPTS; attempt++) {
      try {
        await sendChunk({
          uploadId,
          index,
          total,
          file,
          chunk,
          onLoaded: (loaded) => onBytes(settled + loaded),
        });
        lastError = null;
        break;
      } catch (error) {
        lastError = error;
        const fatal = error.response && error.response.status === 422;

        if (fatal || attempt === CHUNK_ATTEMPTS) break;

        // Rewind the reported progress and let the connection recover.
        onBytes(settled);
        await wait(attempt * 2000);
      }
    }

    if (lastError) throw uploadFailure(lastError, file.name);

    settled += chunk.size;
    onBytes(settled);
  }

  return uploadId;
};

// Submits a registration. `answers` is a map of fieldKey -> value (arrays for
// multi-value fields). `samples` is an array of { uid, file, token,
// performanceType, title }; a sample that already carries a token was uploaded
// by an earlier attempt and is not sent again. `onProgress` receives the upload
// percentage, because a multi-megabyte sample takes minutes on a mobile
// connection and silence reads as a broken form.
export const registerVoiceTalent = async ({
  answers = {},
  samples = [],
  onProgress,
  onSampleUploaded,
  chunkSizeKb,
}) => {
  const chunkBytes = (chunkSizeKb || FALLBACK_CHUNK_KB) * 1024;
  const pending = samples.filter((sample) => sample.file && !sample.token);
  const totalBytes = pending.reduce((sum, sample) => sum + sample.file.size, 0);
  const tokens = samples.map((sample) => sample.token || null);

  let doneBytes = 0;
  const report = (currentFileBytes) => {
    if (!onProgress || totalBytes === 0) return;

    const loaded = doneBytes + currentFileBytes;
    onProgress(Math.min(99, Math.round((loaded * 100) / totalBytes)));
  };

  if (totalBytes > 0) report(0);

  for (let i = 0; i < samples.length; i++) {
    const sample = samples[i];
    if (!sample.file || sample.token) continue;

    const token = await uploadInPieces({
      file: sample.file,
      chunkBytes,
      onBytes: report,
    });

    tokens[i] = token;
    doneBytes += sample.file.size;
    report(0);

    // Remember it upstream: a rejected form should never re-upload the audio.
    if (onSampleUploaded) onSampleUploaded(sample.uid, token);
  }

  // Every byte is in: the wait from here is the server saving the application,
  // which is a different thing to be told about than a stalled upload.
  if (onProgress && totalBytes > 0) onProgress(100);

  const formData = new FormData();

  Object.entries(answers).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;

    if (Array.isArray(value)) {
      value.forEach((item) => formData.append(`answers[${key}][]`, item));
    } else {
      formData.append(`answers[${key}]`, value);
    }
  });

  // Keys follow the position among the uploaded samples, which is how the
  // server pairs a sample with its performance type and title.
  let uploaded = 0;

  samples.forEach((sample, index) => {
    if (!tokens[index]) return;

    formData.append("sample_uploads[]", tokens[index]);
    formData.append(
      `sample_performance_types[${uploaded}]`,
      sample.performanceType || ""
    );
    formData.append(`sample_titles[${uploaded}]`, sample.title || "");
    uploaded++;
  });

  try {
    const response = await apiClient.post("/voice-talents/register", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: FORM_TIMEOUT_MS,
    });

    return response.data;
  } catch (error) {
    // The samples are already on the server at this point, so a failure here is
    // the server saving the application — never a stalled upload. Saying so
    // keeps the advice the applicant gets truthful.
    if (!error.response && (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT")) {
      error.serverTimeout = true;
    }

    error.samplesDelivered = tokens.some(Boolean);
    throw error;
  }
};
